import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, CheckCircle2, ArrowRight, Search, Target, Brain, Building2, Send, Sparkles, Phone, Mail, MapPin, BarChart3, Users, TrendingUp } from 'lucide-react';
import { SPECIALTIES, COMPANY_INFO } from '../data/mock';
import { WA_URL } from '../lib/utils';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import Reveal from '../components/Reveal';
import ParallaxResultados from '../components/ParallaxResultados';
import { ContrateConoscoVideo } from '../components/ContrateConoscoVideo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISC_BENEFITS = [
  { icon: Users, title: 'Melhor aderência cultural', desc: 'Identifique os perfis comportamentais que se alinham à cultura da sua empresa.' },
  { icon: Target, title: 'Maior assertividade', desc: 'Aumente significativamente a precisão das contratações com base em dados comportamentais.' },
  { icon: BarChart3, title: 'Apoio na tomada de decisão', desc: 'Relatórios detalhados que sustentam decisões de contratação, promoção e gestão de equipes.' },
  { icon: TrendingUp, title: 'Redução de turnover', desc: 'Contrate pessoas certas para funções certas, diminuindo a rotatividade.' },
];

const DIFERENCIAIS = [
  { icon: 'national', title: 'Atendimento nacional', desc: 'Conduzimos processos seletivos em todas as regiões do Brasil, 100% online.' },
  { icon: 'agile', title: 'Processo ágil e assertivo', desc: 'SLA de entrega em até 72h, mantendo 95% de taxa de assertividade.' },
  { icon: 'consult', title: 'Atendimento consultivo', desc: 'Acompanhamento personalizado em cada etapa do processo.' },
  { icon: 'empresas', title: '+200 empresas atendidas', desc: 'Referência em recrutamento e seleção em diversos segmentos.' },
  { icon: 'contratacoes', title: '+3.000 contratações', desc: 'Histórico comprovado de contratações de sucesso.' },
];

const EmpresasPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    company: '', cnpj: '', contact_name: '', email: '', phone: '',
    position: '', area: '', location: '', vacancies: '1', modality: 'Híbrido',
    salary: '', description: '',
  });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/proposals`, form);
      toast({ title: 'Solicitação enviada!', description: 'Recebemos sua proposta e entraremos em contato em breve.' });
      setForm({ company: '', cnpj: '', contact_name: '', email: '', phone: '', position: '', area: '', location: '', modality: '', vacancies: '', salary: '', description: '' });
    } catch (err) {
      toast({ title: 'Erro ao enviar', description: 'Tente novamente ou fale conosco pelo WhatsApp.', variant: 'destructive' });
    }
    setSending(false);
  };

  const sendViaWhatsApp = () => {
    axios.post(`${API}/proposals`, { ...form, source: 'Página Para Empresas (via WhatsApp)' }).catch(() => {});
    const msg = `🟧 *Solicitação de Proposta - HM Talent*

*Empresa:* ${form.company}
*CNPJ:* ${form.cnpj}
*Contato:* ${form.contact_name}
*E-mail:* ${form.email}
*Telefone:* ${form.phone}

*Cargo:* ${form.position}
*Área:* ${form.area}
*Localização:* ${form.location}
*Modalidade:* ${form.modality}
*Vagas:* ${form.vacancies}
*Salário:* ${form.salary}

*Descrição/Requisitos:*
${form.description}`;
    window.open(WA_URL(msg), '_blank');
  };

  return (
    <div>
      <Helmet>
        <title>Recrutamento e Seleção para Empresas | HM Talent</title>
        <meta name="description" content="Soluções estratégicas de recrutamento e seleção, hunting especializado e análise comportamental DISC. A HM Talent conecta sua empresa aos melhores talentos com agilidade e assertividade." />
        <meta property="og:title" content="Recrutamento e Seleção para Empresas | HM Talent" />
        <meta property="og:description" content="Recrutamento, seleção, hunting e análise comportamental DISC. Encontre os melhores talentos com a HM Talent — SLA de 72h e garantia de reposição." />
      </Helmet>
      {/* BANNER PRINCIPAL */}
      <section className="hm-gradient text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm px-6 max-w-4xl relative">
          <Reveal>
            <p className="text-sm uppercase tracking-widest text-white/85 font-bold mb-3">Para Empresas</p>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">Soluções Estratégicas de Recrutamento e Seleção</h1>
            <p className="mt-4 text-lg text-white/95 max-w-2xl">Conectamos empresas aos melhores talentos do mercado.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href="#solicitar-proposta" className="btn-hm-white"><Send className="w-5 h-5" /> Solicitar Proposta</a>
              <a href={WA_URL('Olá, quero conhecer as soluções da HM Talent para empresas.')} target="_blank" rel="noopener noreferrer" className="btn-hm-ghost"><MessageCircle className="w-5 h-5" /> Falar no WhatsApp</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VÍDEO "CONTRATE CONOSCO" */}
      <ContrateConoscoVideo />

      {/* RECRUTAMENTO E SELEÇÃO */}
      <section className="section-padding bg-white">
        <div className="container-hm grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="slideRight">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl hm-gradient flex items-center justify-center"><Search className="w-6 h-6 text-white" /></div>
              <span className="text-xs uppercase tracking-widest text-hm-orange font-bold">Serviço 01</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Recrutamento e Seleção</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">Atuamos em todas as etapas do processo seletivo — divulgação, hunting ativo, triagem técnica, entrevistas comportamentais e parecer detalhado. Atendemos posições <strong>operacionais, administrativas, técnicas, estratégicas e de liderança</strong>, entregando os melhores candidatos prontos para a etapa final.</p>
            <ul className="mt-5 space-y-2.5">
              {['Cobertura nacional 100% online','Entrevistas técnicas e comportamentais','SLA garantido em até 72h','Garantia contratual de reposição'].map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-5 h-5 text-hm-orange shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal variant="slideLeft" className="hm-gradient-warm text-white rounded-3xl p-8 card-shadow">
            <Search className="w-12 h-12" />
            <h3 className="text-2xl font-extrabold mt-4">Posições atendidas</h3>
            <p className="text-white/95 mt-2">Atuamos em diversos níveis hierárquicos, sempre com qualidade e profundidade na avaliação.</p>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm font-semibold">
              {['Operacionais','Administrativas','Técnicas','Estratégicas','Gerenciais','Liderança'].map((p, i) => (
                <div key={i} className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-3 py-2.5 text-center">{p}</div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* HUNTING ESPECIALIZADO */}
      <section className="section-padding bg-hm-yellow">
        <div className="container-hm grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="slideRight" className="lg:order-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl hm-gradient flex items-center justify-center"><Target className="w-6 h-6 text-white" /></div>
              <span className="text-xs uppercase tracking-widest text-hm-orange font-bold">Serviço 02</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Hunting Especializado</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">Busca ativa de profissionais qualificados para posições estratégicas e técnicas. Mapeamos profissionais que não estão buscando emprego ativamente, mas que são referência em suas áreas — usando inteligência de mercado, análise de concorrentes e abordagens consultivas.</p>
            <ul className="mt-5 space-y-2.5">
              {['Mapeamento de concorrentes e mercado','Sigilo absoluto na abordagem','Foco em perfis técnicos e específicos','Reporting semanal do processo'].map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-5 h-5 text-hm-orange shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal variant="slideLeft" className="lg:order-1 bg-white rounded-3xl p-8 card-shadow border border-amber-100">
            <Target className="w-12 h-12 text-hm-orange" />
            <h3 className="text-2xl font-extrabold text-slate-900 mt-4">Para quem é indicado</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-hm-orange shrink-0 mt-0.5" /> Vagas estratégicas e de alta complexidade</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-hm-orange shrink-0 mt-0.5" /> Posições técnicas em nichos competitivos</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-hm-orange shrink-0 mt-0.5" /> Substituições confidenciais</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-hm-orange shrink-0 mt-0.5" /> Expansões e abertura de novos times</li>
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ANÁLISE COMPORTAMENTAL SOLIDES DISC */}
      <section className="section-padding bg-white">
        <div className="container-hm">
          <Reveal className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 px-4 py-1.5 rounded-full text-sm font-semibold text-hm-orange mb-3">
              <Brain className="w-4 h-4" /> Serviço 03
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Análise Comportamental Solides DISC Profiler</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">Aplicação de testes comportamentais através da plataforma <strong>Solides DISC Profiler</strong> para compreender o perfil dos candidatos, identificar competências comportamentais e aumentar a assertividade dos processos seletivos.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {DISC_BENEFITS.map((b, i) => (
              <Reveal key={i} delay={i * 80} variant="fadeUp" className={`p-7 rounded-2xl hover-lift card-shadow ${i % 2 === 0 ? 'hm-gradient-warm text-white' : 'bg-hm-yellow border border-amber-100'}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${i % 2 === 0 ? 'bg-white/20' : 'hm-gradient'}`}>
                  <b.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className={`text-lg font-extrabold ${i % 2 === 0 ? 'text-white' : 'text-slate-900'}`}>{b.title}</h3>
                <p className={`text-sm mt-2 leading-relaxed ${i % 2 === 0 ? 'text-white/95' : 'text-slate-700'}`}>{b.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section className="section-padding bg-hm-yellow">
        <div className="container-hm">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Diferenciais</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Diferenciais HM Talent</h2>
            <p className="mt-3 text-slate-700">O que faz da HM Talent a parceira ideal para o seu RH.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {DIFERENCIAIS.map((d, i) => (
              <Reveal key={i} delay={i * 70} variant="fadeUp" className={`p-6 rounded-2xl hover-lift card-shadow text-center ${i === 2 ? 'hm-gradient text-white' : 'bg-white border border-amber-100'}`}>
                <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3 ${i === 2 ? 'bg-white/20' : 'hm-gradient'}`}>
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
                <h3 className={`text-base font-extrabold ${i === 2 ? 'text-white' : 'text-slate-900'}`}>{d.title}</h3>
                <p className={`text-xs mt-2 leading-relaxed ${i === 2 ? 'text-white/95' : 'text-slate-700'}`}>{d.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ESPECIALIDADES */}
      <section className="section-padding bg-white">
        <div className="container-hm">
          <Reveal className="max-w-2xl mb-10">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Áreas de atuação</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Especialidades de Atuação</h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {SPECIALTIES.map((s, i) => (
              <a key={i} href={WA_URL(`Olá, tenho interesse em serviços para empresas do segmento ${s}.`)} target="_blank" rel="noopener noreferrer" className="px-5 py-3 rounded-full bg-hm-yellow border border-amber-200 hover:border-hm-orange hover:hm-gradient hover:text-white text-sm font-semibold text-slate-700 transition-all">{s}</a>
            ))}
          </div>
        </div>
      </section>

      {/* FORMULÁRIO SOLICITAR PROPOSTA */}
      {/* RESULTADOS REAIS - parallax */}
      <ParallaxResultados />

      <section id="solicitar-proposta" className="section-padding bg-hm-yellow">
        <div className="container-hm grid lg:grid-cols-3 gap-8">
          <Reveal variant="slideRight" className="lg:col-span-1">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Solicite uma proposta</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Vamos encontrar o talento ideal</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">Preencha o formulário e nossa equipe entrará em contato em até 24h úteis com uma proposta personalizada.</p>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-hm-orange" /> Resposta em até 24h úteis</div>
              <div className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-hm-orange" /> Sem compromisso inicial</div>
              <div className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-hm-orange" /> Proposta personalizada</div>
            </div>
            <a href={WA_URL('Olá, quero solicitar uma proposta com a HM Talent.')} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-hm-orange font-semibold hover:underline">
              <MessageCircle className="w-4 h-4" /> Ou fale direto no WhatsApp
            </a>
          </Reveal>
          <Reveal variant="slideLeft" className="lg:col-span-2 bg-white border border-amber-200 rounded-3xl p-7 card-shadow">
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
              <div><label className="text-sm font-semibold text-slate-700">Nome da empresa *</label><Input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 h-11" /></div>
              <div><label className="text-sm font-semibold text-slate-700">CNPJ</label><Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className="mt-1 h-11" placeholder="00.000.000/0000-00" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Nome do responsável *</label><Input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 h-11" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Cargo</label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="mt-1 h-11" placeholder="Gerente de RH" /></div>
              <div><label className="text-sm font-semibold text-slate-700">E-mail *</label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Telefone/WhatsApp *</label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11" placeholder="(11) 99999-9999" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Área da vaga *</label>
                <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-semibold text-slate-700">Cidade/UF</label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 h-11" placeholder="São Paulo, SP" /></div>
              <div><label className="text-sm font-semibold text-slate-700">Modalidade</label>
                <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Presencial">Presencial</SelectItem><SelectItem value="Híbrido">Híbrido</SelectItem><SelectItem value="Remoto">Remoto</SelectItem></SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-semibold text-slate-700">Quantidade de vagas</label><Input type="number" min="1" value={form.vacancies} onChange={(e) => setForm({ ...form, vacancies: e.target.value })} className="mt-1 h-11" /></div>
              <div className="md:col-span-2"><label className="text-sm font-semibold text-slate-700">Faixa salarial</label><Input value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="mt-1 h-11" placeholder="R$ 5.000 - R$ 7.000 (ou 'a combinar')" /></div>
              <div className="md:col-span-2"><label className="text-sm font-semibold text-slate-700">Descrição da vaga e principais requisitos *</label><Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} className="mt-1" placeholder="Descreva o cargo, responsabilidades, requisitos técnicos, formação desejada..." /></div>
              <div className="md:col-span-2 flex flex-col sm:flex-row justify-end gap-3">
                <button type="button" onClick={sendViaWhatsApp} data-testid="proposal-whatsapp-btn" className="btn-hm-outline"><MessageCircle className="w-4 h-4" /> Enviar pelo WhatsApp</button>
                <button type="submit" disabled={sending} data-testid="proposal-submit-btn" className="btn-hm disabled:opacity-60">
                  {sending ? 'Enviando...' : <>Solicitar Proposta <Send className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* CHAMADA FINAL */}
      <section className="section-padding hm-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm text-center relative">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-extrabold">Solicite uma proposta comercial</h2>
            <p className="mt-4 text-white/95 max-w-2xl mx-auto text-lg">Nossa equipe está pronta para entender as necessidades da sua empresa e apresentar a melhor solução.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WA_URL('Olá, gostaria de solicitar uma proposta comercial da HM Talent.')} target="_blank" rel="noopener noreferrer" className="btn-hm-white"><MessageCircle className="w-5 h-5" /> Falar no WhatsApp</a>
              <a href="#solicitar-proposta" className="btn-hm-ghost"><Send className="w-5 h-5" /> Solicitar Proposta</a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default EmpresasPage;
